<!-- src/components/ParticipantProgress.vue -->
<!--
  Progress panel for a participant on a BBAS case. Shows which
  sections/fields the participant has filled, skipped, or not yet
  reached. Used inline in the dashboard row when the agent clicks
  a participant name.

  Fetches the form spec (derived sections + fields) from the
  server on mount, then overlays the participant's data + skipped
  maps to compute per-field state. Sections are expandable to show
  individual fields with values (sensitive fields masked).

  Unlike the AABB IndemnitorProgress component, this starts
  expanded — the agent deliberately clicked to see progress, so
  we show it immediately.
-->

<template>
  <div class="progress-panel" @click.stop>

    <!-- Loading state -->
    <div v-if="loading" class="progress-loading">
      <v-progress-circular indeterminate size="20" width="2" color="#635347" />
      <span>Loading form progress…</span>
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="progress-error">
      <v-icon size="16" color="red-darken-1">mdi-alert-circle-outline</v-icon>
      <span>{{ error }}</span>
    </div>

    <!-- Loaded -->
    <template v-else-if="sectionsData.length > 0">

      <!-- Overall summary header -->
      <div class="progress-header">
        <span class="progress-header-summary">
          <strong>{{ overall.done }} of {{ overall.total }}</strong>
          <span class="progress-summary-label">fields complete</span>
          <span class="progress-summary-sections">
            ({{ overall.sectionsComplete }}/{{ overall.sectionsTotal }} sections)
          </span>
        </span>
        <v-progress-linear
          :model-value="overall.total > 0 ? (overall.done / overall.total) * 100 : 0"
          height="6"
          :color="overall.done === overall.total ? '#4caf50' : '#635347'"
          bg-color="#e8e4dc"
          rounded
          class="progress-overall-bar"
        />
      </div>

      <!-- Section list -->
      <div class="section-list">
        <div
          v-for="section in sectionsData"
          :key="section.section_id"
          class="section-row"
          :class="{
            'section-complete': section.isComplete,
          }"
        >
          <!-- Section header (clickable to toggle) -->
          <button
            type="button"
            class="section-header"
            @click="toggleSection(section.section_id)"
          >
            <v-icon class="section-chevron" size="16">
              {{ isExpanded(section.section_id)
                   ? 'mdi-chevron-down'
                   : 'mdi-chevron-right' }}
            </v-icon>

            <span class="section-name">
              {{ sectionLabel(section) }}
            </span>

            <span class="section-progress">
              <span class="section-counts">
                {{ section.doneCount }}/{{ section.totalCount }}
              </span>
              <v-progress-linear
                :model-value="section.totalCount > 0
                  ? (section.doneCount / section.totalCount) * 100
                  : 0"
                height="4"
                :color="section.isComplete ? '#4caf50' : '#635347'"
                bg-color="#e8e4dc"
                class="section-bar"
                rounded
              />
              <v-icon
                v-if="section.isComplete"
                class="section-done-check"
                size="14"
                color="#4caf50"
              >mdi-check-circle</v-icon>
            </span>
          </button>

          <!-- Expanded field list -->
          <div v-if="isExpanded(section.section_id)" class="section-fields">
            <div
              v-for="item in section.items"
              :key="item.field_id || item.group_id"
              class="field-row"
              :class="'field-state-' + item.state"
            >
              <span class="field-state-icon">
                <v-icon v-if="item.state === 'filled'" size="14" color="#4caf50">
                  mdi-check
                </v-icon>
                <v-icon v-else-if="item.state === 'skipped'" size="14" color="grey-darken-1">
                  mdi-minus-circle-outline
                </v-icon>
                <v-icon v-else size="14" color="grey-lighten-1">
                  mdi-circle-small
                </v-icon>
              </span>

              <span class="field-label">{{ fieldLabel(item) }}</span>

              <!-- Value display for filled fields -->
              <template v-if="item.state === 'filled' && item.displayValue !== null">
                <v-icon class="field-value-arrow" size="12">mdi-arrow-right-thin</v-icon>
                <span class="field-value-text">{{ item.displayValue }}</span>
              </template>

              <!-- Sensitive badge -->
              <span
                v-if="item.sensitive"
                class="field-sensitive-badge"
                title="Sensitive — value masked"
              >
                <v-icon size="12">mdi-lock-outline</v-icon>
              </span>

              <!-- Skip reason -->
              <span v-if="item.state === 'skipped'" class="field-state-detail">
                skipped<span v-if="item.skipReason"> — {{ item.skipReason }}</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Empty: no documents selected on the case -->
    <div v-else class="progress-empty">
      No documents selected on this case — nothing to track.
    </div>

  </div>
</template>


<script>
import { auth } from '../firebase';

export default {
  name: 'ParticipantProgress',

  props: {
    // Mongo _id of the case (string).
    caseId:      { type: String, required: true },
    // Participant role: 'defendant', 'indemnitor1', 'indemnitor2'.
    role:        { type: String, required: true },
    // The participant object from the case document. Has .data and
    // .skipped maps. Passed as a prop (not re-fetched) so the
    // progress panel always reflects the same case data the
    // dashboard row is rendering.
    participant: { type: Object, required: true },
  },

  data() {
    return {
      loading: true,
      error: null,
      // The form spec from the server. Populated on mount.
      formSpec: null,
      // Per-section expand state. Seeded to all-expanded on load,
      // then user toggles are respected.
      expandedSections: {},
    };
  },

  computed: {
    // Walk the form spec sections, overlay participant data/skipped
    // to compute per-field state, and build the sections array the
    // template renders.
    sectionsData() {
      if (!this.formSpec || !this.formSpec.sections) return [];

      const data = this.participant?.data || {};
      const skipped = this.participant?.skipped || {};

      const out = [];

      for (const section of this.formSpec.sections) {
        const items = [];
        let doneCount = 0;
        let totalCount = 0;

        for (const item of section.items || []) {
          // Only count editable items toward progress. Read-only
          // cross-references aren't the participant's responsibility.
          if (item.mode !== 'editable') continue;

          if (item.kind === 'group') {
            // Group: check if any member boolean is set in data.
            const anyTrue = (item.members || []).some(
              m => data[m.field_id] === true
            );
            const isSkipped = (item.members || []).some(
              m => skipped[m.field_id]
            );
            const state = isSkipped ? 'skipped'
                        : anyTrue   ? 'filled'
                        : 'unreached';

            totalCount += 1;
            if (state === 'filled' || state === 'skipped') doneCount += 1;

            // For display, show the selected member's label.
            let displayValue = null;
            if (state === 'filled') {
              const selected = (item.members || []).find(
                m => data[m.field_id] === true
              );
              if (selected) {
                displayValue = this._displayLabel(selected.display_label);
              }
            }

            items.push({
              field_id: item.group_id,
              group_id: item.group_id,
              kind: 'group',
              label: (item.members || [])
                .map(m => this._displayLabel(m.display_label))
                .join(' / '),
              state,
              sensitive: false,
              displayValue,
              skipReason: isSkipped
                ? (item.members || [])
                    .map(m => skipped[m.field_id]?.reason)
                    .filter(Boolean)[0] || null
                : null,
            });
          } else {
            // Regular field.
            const fid = item.field_id;
            const raw = data[fid];
            const isSkipped = !!skipped[fid];
            const isFilled = this._isFilled(item, raw);
            const state = isSkipped ? 'skipped'
                        : isFilled  ? 'filled'
                        : 'unreached';

            totalCount += 1;
            if (state === 'filled' || state === 'skipped') doneCount += 1;

            let displayValue = null;
            if (state === 'filled') {
              const formatted = this._formatValue(item, raw);
              displayValue = item.sensitive
                ? this._maskValue(formatted)
                : formatted;
            }

            items.push({
              field_id: fid,
              kind: 'field',
              label: this._displayLabel(item.display_label),
              state,
              sensitive: !!item.sensitive,
              displayValue,
              skipReason: isSkipped
                ? (skipped[fid]?.reason || null)
                : null,
            });
          }
        }

        if (totalCount === 0) continue;

        out.push({
          section_id: section.section_id,
          display_label: section.display_label,
          items,
          doneCount,
          totalCount,
          isComplete: doneCount === totalCount,
        });
      }

      return out;
    },

    overall() {
      let done = 0;
      let total = 0;
      let sectionsComplete = 0;
      for (const sec of this.sectionsData) {
        done += sec.doneCount;
        total += sec.totalCount;
        if (sec.isComplete) sectionsComplete += 1;
      }
      return {
        done,
        total,
        sectionsComplete,
        sectionsTotal: this.sectionsData.length,
      };
    },
  },

  async mounted() {
    await this.fetchFormSpec();
  },

  methods: {
    async fetchFormSpec() {
      this.loading = true;
      this.error = null;
      try {
        const user = auth.currentUser
          || await new Promise((resolve) => {
            const unsub = auth.onAuthStateChanged((u) => {
              unsub();
              resolve(u);
            });
          });
        if (!user) {
          this.error = 'Not authenticated';
          return;
        }
        const token = await user.getIdToken();

        const qs = `role=${encodeURIComponent(this.role)}`;
        const resp = await fetch(
          `/portaldb/db/bbas/cases/${this.caseId}/form-spec?${qs}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await resp.json();

        if (data && data.success) {
          this.formSpec = data.spec;
          // Seed expansion: all sections collapsed except the "active"
          // one — the last incomplete section that has progress. This
          // is the section the participant is currently working through.
          // If nothing has progress, expand the first section.
          this.$nextTick(() => {
            const seed = {};
            const sections = this.sectionsData;
            let activeId = sections.length > 0 ? sections[0].section_id : null;
            for (const sec of sections) {
              seed[sec.section_id] = false;
              if (sec.doneCount > 0 && !sec.isComplete) {
                activeId = sec.section_id;
              }
            }
            if (activeId) seed[activeId] = true;
            this.expandedSections = seed;
          });
        } else {
          this.error = data?.message || 'Failed to load form spec';
        }
      } catch (err) {
        console.error('[ParticipantProgress] fetch error:', err);
        this.error = 'Failed to load form progress';
      } finally {
        this.loading = false;
      }
    },

    // ── Section expansion ────────────────────────────────────

    isExpanded(sectionId) {
      return this.expandedSections[sectionId] === true;
    },

    toggleSection(sectionId) {
      this.expandedSections = {
        ...this.expandedSections,
        [sectionId]: !this.expandedSections[sectionId],
      };
    },

    // ── Display helpers ──────────────────────────────────────

    sectionLabel(section) {
      return section.display_label?.['en-US']
          || section.display_label
          || section.section_id;
    },

    fieldLabel(item) {
      return item.label || item.field_id || '';
    },

    _displayLabel(label) {
      if (!label) return '';
      if (typeof label === 'string') return label;
      return label['en-US'] || Object.values(label)[0] || '';
    },

    // Whether a raw value counts as "filled". Handles composites
    // (address/duration), arrays, and simple scalars.
    _isFilled(item, raw) {
      if (raw === undefined || raw === null || raw === '') return false;
      // Composite (address, duration): check if any part is non-empty.
      if (item.type === 'address' || item.type === 'duration') {
        if (typeof raw === 'object' && !Array.isArray(raw)) {
          return Object.values(raw).some(v => v !== null && v !== undefined && v !== '');
        }
      }
      // Boolean false is a valid filled value for checkboxes.
      if (item.type === 'checkbox') return raw !== undefined && raw !== null;
      return true;
    },

    // Format a raw value for display.
    _formatValue(item, raw) {
      if (raw === undefined || raw === null || raw === '') return null;

      switch (item.type) {
        case 'dob':
        case 'date': {
          if (typeof raw !== 'string') return String(raw);
          const [y, m, d] = raw.split('-');
          if (!y || !m || !d) return raw;
          const months = ['Jan','Feb','Mar','Apr','May','Jun',
                          'Jul','Aug','Sep','Oct','Nov','Dec'];
          return `${months[parseInt(m, 10) - 1] || m} ${parseInt(d, 10)}, ${y}`;
        }
        case 'ssn': {
          const digits = String(raw).replace(/\D/g, '');
          if (digits.length === 9) {
            return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
          }
          return digits || String(raw);
        }
        case 'phone': {
          const digits = String(raw).replace(/\D/g, '');
          const local = digits.length === 11 && digits.startsWith('1')
            ? digits.slice(1)
            : digits;
          if (local.length === 10) {
            return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
          }
          return String(raw);
        }
        case 'currency': {
          const n = Number(raw);
          if (!Number.isFinite(n)) return String(raw);
          return n.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 0,
          });
        }
        case 'address': {
          if (typeof raw === 'object' && !Array.isArray(raw)) {
            return [raw.street, raw.city, raw.state, raw.zip]
              .filter(Boolean).join(', ');
          }
          return String(raw);
        }
        case 'checkbox':
          return raw ? 'Yes' : 'No';
        default:
          if (typeof raw === 'object') return JSON.stringify(raw);
          return String(raw);
      }
    },

    _maskValue(formatted) {
      if (formatted === null) return null;
      return formatted.length > 5
        ? '•'.repeat(formatted.length - 4) + formatted.slice(-4)
        : '•'.repeat(formatted.length);
    },
  },
};
</script>


<style scoped>
.progress-panel {
  background: #fff;
  border: 1px solid #e0d5c0;
  border-radius: 4px;
  padding: 12px 16px;
  margin-top: 8px;
}

/* ── Loading / error / empty ─────────────────────────────── */
.progress-loading,
.progress-error,
.progress-empty {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #888;
  padding: 4px 0;
}
.progress-error {
  color: #c62828;
}

/* ── Overall header ──────────────────────────────────────── */
.progress-header {
  margin-bottom: 12px;
}
.progress-header-summary {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #555;
  margin-bottom: 6px;
}
.progress-header-summary strong {
  color: #333;
  font-size: 14px;
}
.progress-summary-label {
  margin-left: 0;
}
.progress-summary-sections {
  margin-left: 4px;
  color: #999;
  font-size: 12px;
}
.progress-overall-bar {
  max-width: 100%;
}

/* ── Section list ────────────────────────────────────────── */
.section-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.section-row {
  border-radius: 3px;
}
.section-row.section-complete .section-name {
  color: #4caf50;
}

/* ── Section header ──────────────────────────────────────── */
.section-header {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  background: transparent;
  border: 0;
  padding: 6px 4px;
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: #555;
  font-size: 13px;
}
.section-header:hover {
  background: #faf9f6;
}
.section-header:focus-visible {
  outline: 2px solid #d2b48c;
  outline-offset: 1px;
  border-radius: 2px;
}

.section-chevron {
  color: #999;
  flex-shrink: 0;
}
.section-header:hover .section-chevron {
  color: #555;
}

.section-name {
  flex: 1;
  font-weight: 500;
}

.section-progress {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}
.section-counts {
  font-size: 12px;
  color: #888;
  min-width: 30px;
  text-align: right;
}
.section-bar {
  width: 80px;
  flex-shrink: 0;
}
.section-done-check {
  flex-shrink: 0;
}

/* ── Field rows ──────────────────────────────────────────── */
.section-fields {
  padding: 0 0 6px 26px;
}

.field-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 3px 4px;
  font-size: 12px;
  color: #666;
  border-radius: 2px;
}
.field-row:hover {
  background: #fdfcf7;
}

.field-state-icon {
  flex-shrink: 0;
  width: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.field-label {
  flex-shrink: 0;
  max-width: 280px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.field-state-filled .field-label {
  color: #333;
}
.field-state-skipped .field-label {
  color: #999;
  text-decoration: line-through;
}
.field-state-unreached .field-label {
  color: #bbb;
}

.field-value-arrow {
  color: #4caf50;
  flex-shrink: 0;
}
.field-value-text {
  color: #635347;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 320px;
}

.field-sensitive-badge {
  color: #999;
  flex-shrink: 0;
}

.field-state-detail {
  color: #999;
  font-style: italic;
  font-size: 11px;
  margin-left: auto;
}
</style>
